import React, { useState, useEffect, useRef } from 'react'
import styled from '@emotion/styled'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerContent,
  DrawerOverlay,
  CircularProgress,
  Divider,
  CircularProgressLabel,
  Flex,
  Box,
} from '@chakra-ui/core'
import { useSetup } from 'contexts/SetupContext'
import { ChevronUp, Check } from 'lucide-react'
import { SetupStepItem } from './SetupStepItem'
import { isSetupAllowedForUser, STEPS } from 'utils/setupStorage'
import { RootState } from 'constants/interfaces'
import keys from 'constants/keys'
import useLocaleText from 'components/useLocaleText'
import LocaleText from 'components/LocaleText'
import { setIsPersonalView } from 'redux/actions/feed'
import { GetStartedButton } from './SetupStepPopover'

/**
 * Setup Steps Configuration
 * Defines all onboarding steps with their metadata
 */
const SETUP_STEPS = [
  {
    id: STEPS.FEED,
    title: 'connect_email',
    description: 'connect_email_description',
    route: keys.ROUTE_NAMES.NEWS_FEED,
  },
  {
    id: STEPS.ORGANIZATION,
    title: 'create_org_tree',
    description: 'create_org_tree_description',
    route: keys.ROUTE_NAMES.ORGANIZATION_TREE,
  },
  {
    id: STEPS.AI_FEED,
    title: 'ai_feed_personalize',
    description: 'ai_feed_personalize_description',
    route: keys.ROUTE_NAMES.NEWS_FEED,
  },
  {
    id: STEPS.REPORTS,
    title: 'connect_data',
    description: 'connect_data_description',
    route: keys.ROUTE_NAMES.REPORTS,
  },
  {
    id: STEPS.RANKINGS_LIST,
    title: 'set_kpis',
    description: 'set_kpis_description',
    route: keys.ROUTE_NAMES.RANKINGS_LIST,
  },
  {
    id: STEPS.FIND_SALES_OPPORTUNITIES,
    title: 'find_sales_opportunities',
    description: 'find_sales_opportunities_description',
    route: keys.ROUTE_NAMES.SALES_OPPORTUNITIES,
  },
]

/**
 * SetupDrawer Component
 * Bottom drawer that displays setup progress and step timeline
 * Supports both minimized and maximized states
 */
export const SetupDrawer: React.FC = () => {
  const {
    isDrawerMinimized,
    isModalOpen,
    currentStep,
    completedSteps,
    totalSteps,
    setupState,
    isSetupComplete,
    areAllStepsCompleted,
    toggleDrawer,
    maximizeDrawer,
    closeDrawer,
    finalizeSetup,
  } = useSetup()

  const dispatch = useDispatch()
  const uid = useSelector((state: RootState) => state.auth.uid)
  const navigate = useNavigate()
  const [activePopover, setActivePopover] = useState<number | null>(currentStep)
  const prevModalOpenRef = useRef(isModalOpen)
  const wasClosedByOverlayRef = useRef(false)
  const t_step = useLocaleText('step', true)
  const t_of_setup_completed = useLocaleText('of_setup_completed', true)
  const t_continue = useLocaleText('continue', true)
  const t_get_up_and_running = useLocaleText('get_up_and_running', true)
  const t_complete_the_following_steps = useLocaleText('complete_the_following_steps', true)
  const t_your_ai_manager_is_ready = useLocaleText('your_ai_manager_is_ready', true)
  const t_go_to_ai_feed = useLocaleText('go_to_ai_feed', true)
  const setup_completed = useLocaleText('setup_completed', true)
  const all_steps_completed = useLocaleText('all_steps_completed', true)

  const getCurrentStepTitle = () => {
    const step = SETUP_STEPS.find((s) => s.id === currentStep)
    return step?.title || ''
  }

  /**
   * Handles reset of all setup data (debug/testing purposes)
   */
  // const handleResetSetup = () => {
  //   if (window.confirm('Are you sure you want to reset ALL setup data? This will clear setup for all users.')) {
  //     clearAllSetupData()
  //     window.location.reload()
  //   }
  // }

  /**
   * Auto-open popover for first incomplete step when drawer is maximized
   * Don't open popover if modal is already open or all steps completed
   */
  useEffect(() => {
    if (!isDrawerMinimized && setupState && !isModalOpen && !areAllStepsCompleted) {
      const firstIncompleteStep = SETUP_STEPS.find((step) => {
        const stepStatus = setupState.steps[step.id]?.status
        return stepStatus !== 'completed' && stepStatus !== 'skipped'
      })

      if (firstIncompleteStep) {
        // Delay to ensure drawer is fully rendered before showing popover
        setTimeout(() => {
          setActivePopover(firstIncompleteStep.id)
        }, 600)
      }
    } else {
      // Close popover when drawer is minimized or modal is open or all steps completed
      setActivePopover(null)
    }
  }, [isDrawerMinimized, setupState, isModalOpen, areAllStepsCompleted])

  /**
   * Close drawer permanently when setup is completed
   */
  useEffect(() => {
    if (isSetupComplete) {
      console.log('✅ Setup completed! Closing drawer permanently.')
      closeDrawer()
    }
  }, [isSetupComplete, closeDrawer])

  /**
   * Auto-maximize drawer after modal closes (only when modal was just closed)
   * Don't auto-maximize if all steps are completed (completion screen handles that)
   * Skip this if modal was closed by overlay click (handled separately for immediate opening)
   */
  useEffect(() => {
    const wasModalOpen = prevModalOpenRef.current
    prevModalOpenRef.current = isModalOpen

    if (wasModalOpen && !isModalOpen && !isSetupComplete && !areAllStepsCompleted && isDrawerMinimized) {
      // Skip 5-second delay if modal was closed by overlay click
      if (wasClosedByOverlayRef.current) {
        wasClosedByOverlayRef.current = false // Reset flag
        return
      }

      const timeoutId = setTimeout(() => {
        maximizeDrawer()
      }, 5000)

      return () => clearTimeout(timeoutId)
    }
  }, [isModalOpen, isSetupComplete, areAllStepsCompleted, isDrawerMinimized, maximizeDrawer])

  /**
   * Listen for modal closed by overlay click event and immediately maximize drawer
   * This provides better UX when user closes modal by clicking outside
   */
  useEffect(() => {
    const handleModalClosedByOverlay = () => {
      if (isDrawerMinimized && !isSetupComplete && !areAllStepsCompleted) {
        wasClosedByOverlayRef.current = true
        maximizeDrawer()
      }
    }

    window.addEventListener('setupModalClosedByOverlay', handleModalClosedByOverlay)

    return () => {
      window.removeEventListener('setupModalClosedByOverlay', handleModalClosedByOverlay)
    }
  }, [isDrawerMinimized, isSetupComplete, areAllStepsCompleted, maximizeDrawer])

  const handleGoToAIFeed = () => {
    finalizeSetup()
    dispatch(setIsPersonalView(true))
    navigate(keys.ROUTE_NAMES.NEWS_FEED)
  }

  // Don't render drawer if user is not allowed
  if (!isSetupAllowedForUser(uid)) return null

  // Don't render drawer if setup is complete or not initialized
  if (!setupState || isSetupComplete) return null

  return (
    <>
      {isDrawerMinimized ? (
        <MinimizedDrawerContainer>
          <MinimizedHeader>
            <ProgressInfo onClick={maximizeDrawer} style={{ cursor: 'pointer', flex: 1 }}>
              <Flex alignItems="center" style={{ columnGap: '10px' }}>
                <CircularProgress value={(completedSteps / totalSteps) * 100} thickness={0.1} color="pink">
                  <CircularProgressLabel>{((completedSteps / totalSteps) * 100).toFixed(0)}%</CircularProgressLabel>
                </CircularProgress>
                <CurrentStepText>{t_of_setup_completed}</CurrentStepText>
              </Flex>

              {!areAllStepsCompleted && (
                <>
                  <Divider orientation="vertical" height="35px" mx="0px" />
                  <CurrentStepText>
                    {t_step} {currentStep} : <LocaleText text={getCurrentStepTitle()} isLocalUse={true} />
                  </CurrentStepText>
                </>
              )}
              <Divider orientation="vertical" height="35px" mx="0px" />
              <ContinueButton>{areAllStepsCompleted ? 'Finish Setup' : t_continue}</ContinueButton>
            </ProgressInfo>
          </MinimizedHeader>
        </MinimizedDrawerContainer>
      ) : (
        <Drawer isOpen={true} placement="bottom" onClose={toggleDrawer} closeOnOverlayClick={true} closeOnEsc={false}>
          <DrawerOverlay opacity={0.4} />
          <DrawerContent
            maxH="700px"
            borderRadius="20px 20px 0 0"
            boxShadow="0 -4px 20px rgba(0, 0, 0, 0.1)"
            overflow="visible"
            className="chakra-slide"
          >
            <DrawerHeader
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              p="12px 32px"
              borderBottom="1px solid #e2e8f0"
              style={{ direction: 'ltr' }}
            >
              <HeaderContent onClick={toggleDrawer} style={{ cursor: 'pointer', flex: 1 }}>
                <CircularProgress value={(completedSteps / totalSteps) * 100} size="60px" thickness={0.1} color="pink">
                  <CircularProgressLabel>
                    {areAllStepsCompleted ? '100%' : `${completedSteps}/${totalSteps}`}
                  </CircularProgressLabel>
                </CircularProgress>
                <HeaderText>
                  <HeaderTitle>{areAllStepsCompleted ? setup_completed : t_get_up_and_running}</HeaderTitle>
                  <HeaderSubtitle>
                    {areAllStepsCompleted ? all_steps_completed : t_complete_the_following_steps}
                  </HeaderSubtitle>
                </HeaderText>
              </HeaderContent>
              <HeaderActions>
                <ExpandIcon
                  onClick={toggleDrawer}
                  style={{ transform: !isDrawerMinimized ? 'rotate(180deg)' : 'rotate(0deg)', cursor: 'pointer' }}
                />
              </HeaderActions>
            </DrawerHeader>

            <DrawerBody
              p="32px"
              overflowY="visible"
              minH="500px"
              maxH="500px"
              display="flex"
              flexDirection="column"
              justifyContent="flex-end"
              style={{ direction: 'ltr' }}
            >
              {areAllStepsCompleted ? (
                <CompletionContainer>
                  <CompletionBadge>
                    <Check size={55} color="white" strokeWidth={3.5} />
                  </CompletionBadge>
                  <CompletionTitle>{t_your_ai_manager_is_ready}</CompletionTitle>
                  <GetStartedButton
                    fontSize="18px !important"
                    padding="16px 64px !important"
                    onClick={handleGoToAIFeed}
                    background="linear-gradient(135deg, #9559ff 0%,rgb(95, 57, 233) 100%) !important"
                    _hover={{
                      boxShadow: '3px 6px 10px 0px rgba(111, 104, 249, 0.5) !important',
                    }}
                  >
                    {t_go_to_ai_feed}
                  </GetStartedButton>
                  <StepsTimeline style={{ opacity: 0.5, pointerEvents: 'none', marginTop: '40px', width: '100%' }}>
                    <TimelineLine />
                    {SETUP_STEPS.map((step, index) => (
                      <SetupStepItem
                        key={step.id}
                        step={step}
                        stepNumber={index + 1}
                        status={setupState.steps[step.id]?.status || 'pending'}
                        isActive={false}
                        isPopoverOpen={false}
                        onOpenPopover={() => {}}
                        onClosePopover={() => {}}
                        totalSteps={totalSteps}
                      />
                    ))}
                  </StepsTimeline>
                </CompletionContainer>
              ) : (
                <StepsTimeline>
                  <TimelineLine />
                  {SETUP_STEPS.map((step, index) => (
                    <SetupStepItem
                      key={step.id}
                      step={step}
                      stepNumber={index + 1}
                      status={setupState.steps[step.id]?.status || 'pending'}
                      isActive={currentStep === step.id || activePopover === step.id}
                      isPopoverOpen={activePopover === step.id}
                      onOpenPopover={() => {
                        if (activePopover === step.id) {
                          setActivePopover(null)
                        } else {
                          setActivePopover(step.id)
                        }
                      }}
                      onClosePopover={() => setActivePopover(null)}
                      totalSteps={totalSteps}
                    />
                  ))}
                </StepsTimeline>
              )}
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}
    </>
  )
}

const MinimizedDrawerContainer = styled(Box)`
  background: white;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 80px;
  border-radius: 0;
  direction: ltr;
  transition: opacity 0.3s ease-in-out;
`

const HeaderContent = styled(Box)`
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
  direction: ltr;
`

const HeaderText = styled(Box)`
  flex: 1;
`

const HeaderTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 4px;
`

const HeaderSubtitle = styled.p`
  font-size: 14px;
  color: #718096;
  line-height: 1.5;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const MinimizedHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 32px;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: #f7fafc;
  }
`

const ProgressInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 26px;
`

const CurrentStepText = styled.span`
  font-size: 14px;
  color: #718096;
`

const ExpandIcon = styled(ChevronUp)`
  width: 24px;
  height: 24px;
  color: #718096;
`

const StepsTimeline = styled.div`
  position: relative;
  display: flex;
  gap: 100px;
  justify-content: space-between;
  padding: 0 10px;
`

const TimelineLine = styled.div`
  position: absolute;
  top: 17px;
  left: 0;
  right: 0;
  height: 2px;
  background: #e2e8f0;
  z-index: 0;
`

const ContinueButton = styled.button`
  padding: 8px 35px;
  background: white;
  color: #f005c5;
  border: 1px solid #f005c5;
  font-size: 13px;
  border-radius: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-right: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: #f005c5;
    color: white;
  }
`

const CompletionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

const CompletionBadge = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ffffff 0%, #d0dbff 25%, #8a8aff 50%, #2175af 75%, #9f9cd3 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  box-shadow: 0 8px 30px rgba(139, 92, 246, 0.4);
`

const CompletionTitle = styled.h2`
  font-size: 32px;
  color: black;
  margin-bottom: 36px;
  text-align: center;
`
